import React, { useState } from 'react';
import { Shield, LayoutGrid, ClipboardCheck, BarChart2, Grid3x3, Loader2, AlertCircle, RefreshCw, ExternalLink } from 'lucide-react';

import { useZeroTrustFramework } from './hooks/useZeroTrustFramework';
import { useVendorOverlay } from './hooks/useVendorOverlay';
import { useAssessment } from './hooks/useAssessment';
import { useSandboxSync } from './hooks/useSandboxSync';

import GovernanceLayer from './layers/GovernanceLayer';
import PolicyDecisionLayer from './layers/PolicyDecisionLayer';
import PillarGrid from './layers/PillarGrid';
import VisibilityDetailRow from './layers/VisibilityDetailRow';
import PeopleProcessLayer from './layers/PeopleProcessLayer';
import RoadmapTimeline from './layers/RoadmapTimeline';

import VendorSelector from './components/VendorSelector';
import SandboxSyncPanel from './components/SandboxSyncPanel';
import AssessmentPanel from './assessment/AssessmentPanel';
import ExecutiveSummary from './executive/ExecutiveSummary';
import MitreMatrix from './MitreMatrix/MitreMatrix';

import './ZeroTrust.css';

const VIEW_MODES = [
  { id: 'framework', label: 'Framework', icon: LayoutGrid },
  { id: 'assessment', label: 'Assessment', icon: ClipboardCheck },
  { id: 'executive', label: 'Executive', icon: BarChart2 },
  { id: 'mitre', label: 'MITRE Matrix', icon: Grid3x3 }
];

const ZeroTrust = ({ selectedDepartment, departments = [], customerMode = false }) => {
  const [viewMode, setViewMode] = useState('framework');
  const [assessmentPanelOpen, setAssessmentPanelOpen] = useState(false);
  const [syncPanelOpen, setSyncPanelOpen] = useState(false);

  // Hooks
  const { framework, vendors, loading: frameworkLoading, error: frameworkError } = useZeroTrustFramework();
  const sandboxSync = useSandboxSync(framework);
  const { selectedVendorId, overlay, loading: overlayLoading, selectVendor } = useVendorOverlay();
  const {
    assessments,
    gaps,
    summary,
    loading: assessmentLoading,
    saving,
    updateAssessment,
    reload: reloadAssessment
  } = useAssessment(selectedDepartment?.department_id);

  // Filter pillars — exclude VISIBILITY from main grid (shown in VisibilityDetailRow)
  const mainPillars = (framework?.pillars || []).filter(
    p => p.pillar_code !== 'VISIBILITY'
  );

  // Switch to assessment mode — open the panel
  const handleAssessmentMode = () => {
    setViewMode('assessment');
    setAssessmentPanelOpen(true);
  };

  const handleViewChange = (mode) => {
    if (mode === 'assessment') {
      setAssessmentPanelOpen(true);
    }
    setViewMode(mode);
  };

  if (frameworkLoading) {
    return (
      <div className="zt-container">
        <div className="zt-loading">
          <Loader2 size={32} className="spin" />
          <p>Loading Zero Trust Framework...</p>
        </div>
      </div>
    );
  }

  if (frameworkError) {
    return (
      <div className="zt-container">
        <div className="zt-error">
          <AlertCircle size={32} />
          <p>Failed to load framework: {frameworkError}</p>
          <button
            className="btn-secondary"
            onClick={() => window.location.reload()}
          >
            <RefreshCw size={14} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="zt-container">
      {/* Header */}
      <div className="zt-header">
        <div className="zt-header-left">
          <div className="zt-header-title">
            <Shield size={22} style={{ color: 'var(--color-primary)' }} />
            {customerMode ? (
              <div>
                <h1>Zero Trust Security Assessment</h1>
                <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary, #94a3b8)' }}>
                  Evaluate your organization's zero trust security coverage
                </p>
              </div>
            ) : (
              <h1>Zero Trust Framework</h1>
            )}
          </div>

          {/* View Mode Tabs — hide for customers */}
          {!customerMode && (
            <div className="zt-view-tabs">
              {VIEW_MODES.map(mode => {
                const Icon = mode.icon;
                const isDisabled = mode.id === 'assessment' && !selectedDepartment;
                return (
                  <button
                    key={mode.id}
                    className={`zt-view-tab ${viewMode === mode.id ? 'active' : ''}`}
                    onClick={() => handleViewChange(mode.id)}
                    disabled={isDisabled}
                    title={isDisabled ? 'Select a department to use assessment mode' : ''}
                  >
                    <Icon size={14} />
                    {mode.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {!customerMode && (
          <div className="zt-header-right">
            {/* Vendor overlay selector */}
            <VendorSelector
              vendors={vendors}
              selectedVendorId={selectedVendorId}
              onSelect={selectVendor}
              loading={overlayLoading}
            />

            {/* Vendor overlay active badge */}
            {overlay && (
              <span className="zt-vendor-badge">
                <Shield size={12} />
                {overlay.vendor?.vendor_name || 'Vendor'} overlay active
              </span>
            )}

            {/* Preview sandbox (opens in new tab) */}
            <a
              href="/sandbox"
              target="_blank"
              rel="noopener noreferrer"
              className="zt-sync-btn"
              title="Preview Sandbox (opens in new tab)"
            >
              <ExternalLink size={14} />
              Preview Sandbox
            </a>

            {/* Sandbox sync button */}
            <button
              className={`zt-sync-btn ${sandboxSync.isStale ? 'zt-sync-btn--stale' : ''}`}
              onClick={() => setSyncPanelOpen(true)}
              title="Sandbox Sync Settings"
            >
              <RefreshCw size={14} />
              Sync
              {sandboxSync.isStale && <span className="zt-sync-dot" />}
            </button>

            {/* Quick assessment button when framework view */}
            {viewMode === 'framework' && selectedDepartment && (
              <button
                className="btn-secondary"
                onClick={handleAssessmentMode}
              >
                <ClipboardCheck size={14} />
                Assess
              </button>
            )}
          </div>
        )}
      </div>

      {/* Main content */}
      <div className="zt-scroll-area">
        {viewMode === 'executive' ? (
          <ExecutiveSummary
            summary={summary}
            gaps={gaps}
            department={selectedDepartment}
            vendorOverlay={overlay}
            loading={assessmentLoading}
          />
        ) : viewMode === 'mitre' ? (
          <MitreMatrix selectedDepartment={selectedDepartment} />
        ) : (
          <>
            {/* Governance Layer — top horizontal bar */}
            <GovernanceLayer overlay={overlay} />

            {/* Policy Decision Point */}
            <PolicyDecisionLayer overlay={overlay} />

            {/* Pillar Grid — 5 enforcement pillars (excluding VISIBILITY) */}
            {mainPillars.length > 0 && (
              <PillarGrid
                pillars={mainPillars}
                assessments={assessments}
                vendorOverlay={overlay}
                assessmentMode={viewMode === 'assessment'}
                onUpdateAssessment={updateAssessment}
              />
            )}

            {/* Visibility & Analytics Row */}
            <VisibilityDetailRow overlay={overlay} />

            {/* People & Process Layer */}
            <PeopleProcessLayer />

            {/* Roadmap Timeline */}
            <RoadmapTimeline vendorId={selectedVendorId} />
          </>
        )}
      </div>

      {/* Assessment Slide-In Panel */}
      <AssessmentPanel
        isOpen={assessmentPanelOpen && viewMode === 'assessment'}
        onClose={() => {
          setAssessmentPanelOpen(false);
          setViewMode('framework');
        }}
        department={selectedDepartment}
        framework={framework}
        assessments={assessments}
        gaps={gaps}
        summary={summary}
        loading={assessmentLoading}
        saving={saving}
        onUpdateAssessment={updateAssessment}
        onReload={reloadAssessment}
      />

      {/* Sandbox Sync Panel */}
      <SandboxSyncPanel
        isOpen={syncPanelOpen}
        onClose={() => setSyncPanelOpen(false)}
        {...sandboxSync}
      />
    </div>
  );
};

export default ZeroTrust;
